import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  PurlyService as IPurlyService,
  PurlyErrorResponse,
  PurlyHelloResponse,
  PurlyInitializationResult,
  PurlyServiceConfig,
  PurlySignedUrlInfo,
  EsriRequestError
} from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, firstValueFrom, throwError } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';
import esriConfig from '@arcgis/core/config';
import type { RequestInterceptor } from "@arcgis/core/request/types";
import { EsriInterceptor } from '../arcgis/esri.interceptor';

/**
 * Service for integrating with the Purly URL signing service.
 * Handles authentication, configuration discovery, and basemap URL management.
 *
 * This service acts as a startup gate - it must successfully initialize
 * before the application can access basemaps and other signed resources.
 *
 * The service also handles automatic URL refresh based on expiration times (exp)
 * from the PURLY response. When URLs are about to expire, the service will
 * automatically re-initialize and emit updated URLs through initializationResult$.
 */
@Injectable({
  providedIn: 'root'
})
export class PurlyService implements IPurlyService, OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(NGXLogger);
  private readonly esriInterceptor = inject(EsriInterceptor);

  private readonly _initializationResult =
    new BehaviorSubject<PurlyInitializationResult | null>(null);
  private readonly _isInitialized = new BehaviorSubject<boolean>(false);

  private _currentConfig: PurlyServiceConfig | null = null;
  private _expirationTimer: ReturnType<typeof setTimeout> | null = null;
  private _cachedExpiration: {
    urlHash: string;
    result: number;
  } | null = null;
  // Tracks the interceptor instance by reference (not array index) so that
  // removal stays correct even if other code mutates esriConfig.request.interceptors.
  // See JMKW-1863 — the prior index-based tracking was order-fragile.
  private _purlyInterceptor: RequestInterceptor | null = null;

  /** Default timeout in milliseconds for API calls */
  private readonly DEFAULT_TIMEOUT_MS = 10000;

  /** Number of retry attempts for failed API calls */
  private readonly RETRY_ATTEMPTS = 2;

  /** Buffer time in seconds before expiration to trigger refresh */
  private readonly REFRESH_BUFFER_SECONDS = 60;

  /** Conversion factor: milliseconds per second */
  private readonly MS_PER_SECOND = 1000;

  /** Conversion factor: milliseconds per minute */
  private readonly MS_PER_MINUTE = 60000;

  /** HTTP Content-Type header value */
  private readonly CONTENT_TYPE_JSON = 'application/json';

  /** Observable that emits the initialization result */
  public readonly initializationResult$ =
    this._initializationResult.asObservable();

  /** Observable that emits true when the service has been initialized */
  public readonly isInitialized$ = this._isInitialized.asObservable();

  /** Current initialization result, if available */
  public get initializationResult(): PurlyInitializationResult | null {
    return this._initializationResult.value;
  }

  /** Whether the service has been successfully initialized */
  public get isInitialized(): boolean {
    return this._isInitialized.value;
  }

  /**
   * Initialize the Purly service by calling the hello endpoint
   * and retrieving the client configuration and signed URLs.
   *
   * @param config Purly service configuration
   * @returns Promise that resolves with initialization result
   */
  public async initialize(
    config: PurlyServiceConfig
  ): Promise<PurlyInitializationResult> {
    this.logger.info('[PurlyService] Starting initialization', {
      config: this.sanitizeConfig(config)
    });

    // Store config for potential re-initialization on expiration
    this._currentConfig = config;

    if (!config.enabled) {
      this.logger.info('[PurlyService] Disabled by configuration');
      const result: PurlyInitializationResult = {
        success: false,
        error: 'Purly service is disabled'
      };
      this._initializationResult.next(result);
      return result;
    }

    try {
      const helloResponse = await firstValueFrom(this.callHelloEndpoint(config));

      if (!helloResponse) {
        throw new Error('No response received from hello endpoint');
      }

      // Flatten URLs once and reuse
      const flattenedUrls =
        helloResponse.root || this.flattenPurlyResponse(helloResponse);

      // Ensure root array is populated
      if (!helloResponse.root) {
        helloResponse.root = flattenedUrls;
      }

      // Categorize signed URLs by type
      const signedUrls = this.categorizeSignedUrls(helloResponse);

      // Find earliest expiration time from flattened URLs
      const earliestExpiration = this.findEarliestExpiration(flattenedUrls);

      // Build result using builder pattern for clarity
      const result = this.buildSuccessResult(
        helloResponse,
        signedUrls,
        earliestExpiration
      );

      this.logger.info('[PurlyService] Initialization successful', {
        clientId: helloResponse.clid,
        urlCount: flattenedUrls.length,
        categories: {
          basemaps: Object.keys(signedUrls.basemaps).length,
          tileServices: Object.keys(signedUrls.tileServices).length
        },
        earliestExpiration: earliestExpiration
          ? new Date(earliestExpiration * this.MS_PER_SECOND).toISOString()
          : 'N/A'
      });

      this._initializationResult.next(result);
      this._isInitialized.next(true);

      // Set up expiration timer
      this.scheduleRefresh(earliestExpiration);

      // Set up request interceptor for tile requests
      // Pass the actual signed URLs so we can match the correct domains
      this.setupPurlyRequestInterceptor(config, flattenedUrls);

      this.logger.trace('*** PURLY RESULT ***', result);
      return result;
    } catch (error) {
      return this.handleInitializationError(error);
    }
  }

  /**
   * Call the hello endpoint to retrieve client configuration
   *
   * @param config Purly service configuration
   * @returns Observable with hello response
   */
  private callHelloEndpoint(
    config: PurlyServiceConfig
  ): Observable<PurlyHelloResponse> {
    const url = `${config.baseUrl}/hello`;
    const timeoutMs = config.timeoutMs || this.DEFAULT_TIMEOUT_MS;

    // New authorization strategy: clientId:keyVersion:apiKey
    const authToken = `${config.clientId}:${config.keyVersion}:${config.apiKey}`;
    const headers = new HttpHeaders({
      'Content-Type': this.CONTENT_TYPE_JSON,
      Authorization: `Bearer ${authToken}`
    });

    this.logger.trace('[PurlyService] Calling hello endpoint', {
      url,
      clientId: config.clientId,
      keyVersion: config.keyVersion
    });

    return this.http.post<PurlyHelloResponse>(url, {}, { headers }).pipe(
      timeout(timeoutMs),
      retry(this.RETRY_ATTEMPTS),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('[PurlyService] Hello endpoint call failed', {
          status: error.status,
          message: error.message,
          url
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Set up a single ArcGIS request interceptor that owns BOTH the auth `before` handler
   * and the JMKW tile error `error` handler.
   *
   * ArcGIS picks exactly one matching interceptor per URL (see `getInterceptor` in
   * `@arcgis/core/core/urlUtils`). Registering two separately — one for auth, one for
   * error classification — meant that only the first-in-array won, and after the first
   * URL refresh the order flipped and tile fetches lost their `Authorization` header.
   * Merging the two responsibilities here removes the race entirely (JMKW-1863).
   *
   * The `error` callback delegates to {@link EsriInterceptor.handleJmkwTileError} so that
   * plugins which registered URL-specific handlers via `EsriInterceptor.registerErrorHandler`
   * continue to fire as before.
   *
   * @param config Purly service configuration containing auth credentials
   * @param signedUrls Array of signed URLs used to extract the matching tile service domains
   */
  private setupPurlyRequestInterceptor(
    config: PurlyServiceConfig,
    signedUrls: PurlySignedUrlInfo[]
  ): void {
    // Remove any existing interceptor first
    this.removePurlyRequestInterceptor();

    // Initialize interceptors array if needed
    esriConfig.request.interceptors ??= [];

    // Create the composite auth token
    const authToken = `${config.clientId}:${config.keyVersion}:${config.apiKey}`;

    // Extract unique origins/domains from signed URLs
    // These are the actual URLs that will be requested by ArcGIS
    const origins = new Set<string>();
    signedUrls.forEach(urlInfo => {
      try {
        const url = new URL(urlInfo.url);
        // Match the full origin (protocol + domain + port)
        origins.add(url.origin);
      } catch (error) {
        this.logger.warn('[PurlyService] Failed to parse signed URL', { url: urlInfo.url });
      }
    });

    if (origins.size === 0) {
      this.logger.warn('[PurlyService] No valid origins found in signed URLs, skipping interceptor setup');
      return;
    }

    // Create regex pattern that matches any of the signed URL origins.
    // The boundary prevents matching lookalike hosts such as
    // https://tiles.arcgis.com.evil.example.
    const escapedOriginPatterns = Array.from(origins).map(origin =>
      `${origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:/|$)`
    );
    const urlPattern = new RegExp(`^(${escapedOriginPatterns.join('|')})`);

    this.logger.info('[PurlyService] Configuring request interceptor', {
      origins: Array.from(origins),
      pattern: urlPattern.toString()
    });

    const interceptor: RequestInterceptor = {
      // Match requests to any signed URL origin
      urls: urlPattern,
      // 'before' adds the Authorization header to every outgoing signed tile request
      before: (params) => {
        params.requestOptions = params.requestOptions || {};
        params.requestOptions.headers = params.requestOptions.headers || {};
        params.requestOptions.headers['Authorization'] = `Bearer ${authToken}`;

        if (!params.requestOptions.headers['Authorization']) {
          this.logger.warn('[PurlyService] Failed to add Authorization header to Purly request', {
            url: params.url
          });
        }
      },
      // 'error' classifies the response and dispatches to any handlers registered
      // via EsriInterceptor.registerErrorHandler (used by plugins for URL-specific
      // error handling). Co-locating with `before` ensures ArcGIS' single-interceptor
      // selection always picks the interceptor that knows about both auth and errors.
      error: (error: unknown) => {
        this.esriInterceptor.handleJmkwTileError(error as EsriRequestError);
      }
    };

    this._purlyInterceptor = interceptor;
    esriConfig.request.interceptors.push(interceptor);

    this.logger.info('[PurlyService] Request interceptor configured for tile requests', {
      totalInterceptors: esriConfig.request.interceptors.length
    });

    // Verify the interceptor was actually added with both callbacks
    const addedInterceptorIndex = esriConfig.request.interceptors.indexOf(interceptor);
    const addedInterceptor =
      addedInterceptorIndex === -1
        ? undefined
        : esriConfig.request.interceptors[addedInterceptorIndex];
    if (
      !addedInterceptor ||
      this._purlyInterceptor !== addedInterceptor ||
      !addedInterceptor.before ||
      !addedInterceptor.error
    ) {
      this.logger.error('[PurlyService] FAILED to add interceptor properly!', {
        interceptorIndex: addedInterceptorIndex,
        storedByReference: this._purlyInterceptor === addedInterceptor,
        hasBefore: !!addedInterceptor?.before,
        hasError: !!addedInterceptor?.error
      });
    }
  }

  /**
   * Remove the Purly request interceptor if it exists.
   *
   * Uses `indexOf` on the stored interceptor reference rather than a cached array index,
   * so removal stays correct even when other code mutates `esriConfig.request.interceptors`
   * in between install and remove (JMKW-1863).
   */
  private removePurlyRequestInterceptor(): void {
    if (!this._purlyInterceptor || !esriConfig.request.interceptors) {
      return;
    }

    const idx = esriConfig.request.interceptors.indexOf(this._purlyInterceptor);
    if (idx !== -1) {
      esriConfig.request.interceptors.splice(idx, 1);
      this.logger.trace('[PurlyService] Removed request interceptor');
    } else {
      this.logger.warn('[PurlyService] Stored interceptor was not found in esriConfig.request.interceptors');
    }
    this._purlyInterceptor = null;
  }

  /**
   * Strategy pattern: URL extractors for different tile types
   * Each extractor knows how to get its specific URLs from the response
   */
  private readonly TILE_URL_EXTRACTORS: Array<
    (response: PurlyHelloResponse) => PurlySignedUrlInfo[]
  > = [
    // Cultural extractor
    (r) => r.tiles?.cultural || [],
    // Obstacle extractor
    (r) => r.tiles?.obstacle || [],
    // Navigation InFlight extractor
    (r) => r.tiles?.navigation?.InFlight || [],
    // Navigation Operations extractor
    (r) => r.tiles?.navigation?.Operations || [],
    // Terrain dark extractor
    (r) => r.tiles?.terrain?.dark || [],
    // Terrain light extractor
    (r) => r.tiles?.terrain?.light || []
  ];

  /**
   * Flatten the new hierarchical Purly response into a backward-compatible root array
   *
   * @param response The Purly hello response
   * @returns Flattened array of all signed URL info
   */
  private flattenPurlyResponse(
    response: PurlyHelloResponse
  ): PurlySignedUrlInfo[] {
    const urls: PurlySignedUrlInfo[] = [];

    // Flatten basemaps
    if (response.basemaps) {
      Object.values(response.basemaps).forEach((urlInfoArray) => {
        if (urlInfoArray) {
          urls.push(...urlInfoArray);
        }
      });
    }

    // Flatten tiles using strategy pattern
    this.TILE_URL_EXTRACTORS.forEach((extractor) => {
      urls.push(...extractor(response));
    });

    return urls;
  }

  /**
   * Categorize signed URLs by their purpose (basemaps, tile services, etc.)
   *
   * Selection Strategy: When multiple URLs are provided for a service, this method
   * selects the first URL. This is based on the current API contract assumption that
   * each service type provides a single URL per category.
   *
   * ASSUMPTION: The PURLY API currently returns single-element arrays for each service.
   * If this changes (e.g., for AIRAC transition periods, failover URLs, or multi-portal
   * support), the selectUrlFromArray() method will log warnings for investigation.
   *
   * FUTURE CONSIDERATION: If multiple URLs become common, implement intelligent selection
   * based on expiration times, isAirac flags, or portalId values.
   *
   * @param response The complete Purly hello response
   * @returns Categorized URLs keyed by service ID
   */
  private categorizeSignedUrls(response: PurlyHelloResponse): {
    basemaps: Record<string, PurlySignedUrlInfo>;
    tileServices: Record<string, PurlySignedUrlInfo>;
  } {
    const basemaps: Record<string, PurlySignedUrlInfo> = {};
    const tileServices: Record<string, PurlySignedUrlInfo> = {};

    // Process basemaps
    if (response.basemaps) {
      for (const [serviceName, urlInfoArray] of Object.entries(
        response.basemaps
      ) as [string, PurlySignedUrlInfo[]][]) {
        const selectedUrl = this.selectUrlFromArray(
          urlInfoArray,
          `basemap.${serviceName}`
        );
        if (selectedUrl) {
          basemaps[serviceName] = selectedUrl;
        }
      }
    }

    // Process tiles
    if (response.tiles) {
      // Cultural
      const culturalUrl = this.selectUrlFromArray(
        response.tiles.cultural,
        'tiles.cultural'
      );
      if (culturalUrl) {
        tileServices['cultural'] = culturalUrl;
      }

      // Navigation - organized by persona
      if (response.tiles.navigation) {
        const navigation = response.tiles.navigation;

        const inFlightUrl = this.selectUrlFromArray(
          navigation.InFlight,
          'tiles.navigation.InFlight'
        );
        if (inFlightUrl) {
          tileServices['InFlight'] = inFlightUrl;
        }

        const operationsUrl = this.selectUrlFromArray(
          navigation.Operations,
          'tiles.navigation.Operations'
        );
        if (operationsUrl) {
          tileServices['Operations'] = operationsUrl;
        }
      }

      // Obstacle
      const obstacleUrl = this.selectUrlFromArray(
        response.tiles.obstacle,
        'tiles.obstacle'
      );
      if (obstacleUrl) {
        tileServices['obstacle'] = obstacleUrl;
      }

      // Terrain - organized by theme
      if (response.tiles.terrain) {
        const terrain = response.tiles.terrain;

        const darkUrl = this.selectUrlFromArray(
          terrain.dark,
          'tiles.terrain.dark'
        );
        if (darkUrl) {
          tileServices['dark'] = darkUrl;
        }

        const lightUrl = this.selectUrlFromArray(
          terrain.light,
          'tiles.terrain.light'
        );
        if (lightUrl) {
          tileServices['light'] = lightUrl;
        }
      }
    }

    return { basemaps, tileServices };
  }

  /**
   * Select a URL from an array of signed URLs with validation
   *
   * Selection Strategy: Returns the first URL in the array. This assumes the PURLY API
   * provides URLs in priority order (most preferred first).
   *
   * VALIDATION: Logs a warning if multiple URLs are present. This serves as an early
   * detection mechanism for API contract changes or unexpected scenarios like:
   * - AIRAC transition periods (old + new cycle data)
   * - Failover/redundancy URLs
   * - Multi-portal data sources
   * - Load balancing endpoints
   *
   * The warning includes full URL details to enable analysis and inform whether a more
   * sophisticated selection strategy (based on expiration, isAirac, or portalId) is needed.
   *
   * @param urlArray Array of signed URLs (may be undefined)
   * @param serviceName Name of the service for logging purposes
   * @returns The selected URL or undefined if array is empty/undefined
   */
  private selectUrlFromArray(
    urlArray: PurlySignedUrlInfo[] | undefined,
    serviceName: string
  ): PurlySignedUrlInfo | undefined {
    if (!urlArray || urlArray.length === 0) {
      return undefined;
    }

    // Warn if multiple URLs provided - this may indicate an API contract change
    if (urlArray.length > 1) {
      this.logger.warn(
        `[PurlyService] Multiple URLs provided for ${serviceName}. Expected single URL, got ${urlArray.length}. Using first URL.`,
        {
          serviceName,
          urlCount: urlArray.length,
          urls: urlArray.map((u) => ({ url: u.url, svc: u.svc, exp: u.exp }))
        }
      );
    }

    return urlArray[0];
  }

  /**
   * Extract a meaningful error message from various error types
   *
   * @param error The error object
   * @returns Human-readable error message
   */
  private extractErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.error && typeof error.error === 'object') {
        const purlyError = error.error as PurlyErrorResponse;
        if (purlyError.message) {
          return `Purly API Error: ${purlyError.message}`;
        }
      }
      return `HTTP ${error.status}: ${error.message}`;
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Unknown error occurred';
  }

  /**
   * Handle initialization errors consistently
   *
   * @param error The error object
   * @returns Error initialization result
   */
  private handleInitializationError(error: unknown): PurlyInitializationResult {
    const errorMessage = this.extractErrorMessage(error);
    this.logger.error('[PurlyService] Initialization failed', {
      error: errorMessage
    });

    const result: PurlyInitializationResult = {
      success: false,
      error: errorMessage
    };

    this._initializationResult.next(result);
    return result;
  }

  /**
   * Create a sanitized copy of the config for logging (removes sensitive data)
   *
   * @param config Original configuration
   * @returns Sanitized configuration safe for logging
   */
  private sanitizeConfig(
    config: PurlyServiceConfig
  ): Partial<PurlyServiceConfig> {
    return {
      baseUrl: config.baseUrl,
      clientId: config.clientId,
      keyVersion: config.keyVersion,
      enabled: config.enabled,
      timeoutMs: config.timeoutMs,
      apiKey: config.apiKey ? '[REDACTED]' : undefined
    };
  }

  /**
   * Get signed URLs for basemaps
   *
   * @returns Record of signed basemap URLs keyed by service ID, or empty object if not initialized
   */
  public getBasemapUrls(): Record<string, PurlySignedUrlInfo> {
    const result = this.initializationResult;
    return result?.success ? result.signedUrls?.basemaps || {} : {};
  }

  /**
   * Get signed URLs for tile services
   *
   * @returns Record of signed tile service URLs keyed by service ID, or empty object if not initialized
   */
  public getTileServiceUrls(): Record<string, PurlySignedUrlInfo> {
    const result = this.initializationResult;
    return result?.success ? result.signedUrls?.tileServices || {} : {};
  }

  /**
   * Get all signed URLs
   *
   * @returns Array of all signed URLs, or empty array if not initialized
   */
  public getAllSignedUrls(): PurlySignedUrlInfo[] {
    const result = this.initializationResult;
    if (!result?.success || !result.configuration?.root) {
      return [];
    }
    return result.configuration.root;
  }

  /**
   * Build a successful initialization result
   * Builder pattern for complex object construction
   *
   * @param configuration Purly hello response
   * @param signedUrls Categorized signed URLs
   * @param earliestExpiration Earliest expiration timestamp
   * @returns Successful initialization result
   */
  private buildSuccessResult(
    configuration: PurlyHelloResponse,
    signedUrls: {
      basemaps: Record<string, PurlySignedUrlInfo>;
      tileServices: Record<string, PurlySignedUrlInfo>;
    },
    earliestExpiration: number | undefined
  ): PurlyInitializationResult {
    return {
      success: true,
      configuration,
      signedUrls,
      earliestExpiration
    };
  }

  /**
   * Find the earliest expiration timestamp across all signed URLs
   * Uses memoization to avoid recalculating for the same URL array
   *
   * @param urls Array of signed URLs
   * @returns Earliest expiration timestamp in seconds, or undefined if none found
   */
  private findEarliestExpiration(
    urls: PurlySignedUrlInfo[]
  ): number | undefined {
    if (urls.length === 0) {
      return undefined;
    }

    // Create a hash of the URLs for cache comparison
    // This avoids memory leaks from holding array references
    const urlHash = urls.map(u => `${u.url}:${u.exp}`).join('|');

    // Check cache for performance optimization
    if (this._cachedExpiration?.urlHash === urlHash) {
      return this._cachedExpiration.result;
    }

    // Find the minimum exp value
    const earliestExp = Math.min(...urls.map((url) => url.exp));

    // Cache the result with hash instead of array reference
    this._cachedExpiration = { urlHash, result: earliestExp };

    this.logger.trace('[PurlyService] Calculated earliest expiration', {
      earliestExp,
      date: new Date(earliestExp * this.MS_PER_SECOND).toISOString(),
      totalUrls: urls.length
    });

    return earliestExp;
  }

  /**
   * Schedule a refresh of Purly URLs based on expiration time
   * The refresh is triggered 60 seconds before expiration to ensure seamless transition
   *
   * @param expirationTimestamp Unix timestamp in seconds when URLs expire
   */
  private scheduleRefresh(expirationTimestamp: number | undefined): void {
    // Clear any existing timer
    this.clearExpirationTimer();

    if (!expirationTimestamp || !this._currentConfig) {
      this.logger.trace(
        '[PurlyService] No expiration timer scheduled (no timestamp or config)'
      );
      return;
    }

    const now = Math.floor(Date.now() / this.MS_PER_SECOND);
    const timeUntilRefresh = Math.max(
      0,
      (expirationTimestamp - this.REFRESH_BUFFER_SECONDS - now) *
        this.MS_PER_SECOND
    );

    if (timeUntilRefresh === 0) {
      this.logger.warn(
        '[PurlyService] URLs have already expired or will expire soon, refreshing immediately'
      );
      this.refreshUrls();
      return;
    }

    this.logger.info('[PurlyService] Scheduled URL refresh', {
      expirationTime: new Date(
        expirationTimestamp * this.MS_PER_SECOND
      ).toISOString(),
      refreshTime: new Date(
        (expirationTimestamp - this.REFRESH_BUFFER_SECONDS) * this.MS_PER_SECOND
      ).toISOString(),
      timeUntilRefreshMs: timeUntilRefresh,
      timeUntilRefreshMinutes: Math.round(timeUntilRefresh / this.MS_PER_MINUTE)
    });

    this._expirationTimer = setTimeout(() => {
      this.logger.info(
        '[PurlyService] Expiration timer triggered, refreshing URLs'
      );
      this.refreshUrls();
    }, timeUntilRefresh);
  }

  /**
   * Refresh Purly URLs by re-initializing the service
   * This will fetch new signed URLs and notify all subscribers
   */
  private async refreshUrls(): Promise<void> {
    if (!this._currentConfig) {
      this.logger.error(
        '[PurlyService] Cannot refresh - no configuration stored'
      );
      return;
    }

    this.logger.info('[PurlyService] Refreshing Purly URLs due to expiration');

    try {
      await this.initialize(this._currentConfig);
      this.logger.info('[PurlyService] URL refresh completed successfully');
    } catch (error) {
      this.logger.error('[PurlyService] URL refresh failed', { error });
    }
  }

  /**
   * Clear the expiration timer
   */
  private clearExpirationTimer(): void {
    if (this._expirationTimer) {
      clearTimeout(this._expirationTimer);
      this._expirationTimer = null;
    }
  }

  /**
   * Cleanup method to clear timers and cache when service is destroyed
   * Implements OnDestroy lifecycle hook
   */
  public ngOnDestroy(): void {
    this.clearExpirationTimer();
    this.removePurlyRequestInterceptor();
    this._cachedExpiration = null;
  }
}
